import type { BaseEntity } from "../../../UniversalCRMTable/types";
import {
  useCompositionStableSlices,
  type CompositionStableSlices,
} from "./useCompositionStableSlices";
import {
  useCompositionVolatileSlices,
  type CompositionVolatileSlices,
} from "./useCompositionVolatileSlices";
import type { CompositionContextValueParams } from "./compositionContextValueTypes";

/**
 * The context value split into INDEPENDENTLY memoized slices, along the
 * volatile/stable seam:
 *
 * - STABLE   — pageConfig, auth, navigation, handlers, callbacks, mutations,
 *              view mode, infinite-scroll toggle
 * - VOLATILE — dataState, entityState, filterState, exportState
 *
 * The whole point: a slice must only change when its OWN inputs change. Built
 * as one 85-dependency `useMemo`, every sub-object was re-allocated whenever
 * ANY of the 85 moved — so a keystroke in the search box handed a board view a
 * brand-new `dataState` and `pageConfig` it did not care about, and the entire
 * page tree re-rendered. Board views and headers read almost entirely from the
 * stable half.
 */
export type CompositionContextSlices<T extends BaseEntity> = CompositionStableSlices<T> &
  CompositionVolatileSlices<T>;

export type { CompositionStableSlices } from "./useCompositionStableSlices";
export type { CompositionVolatileSlices } from "./useCompositionVolatileSlices";
export { useCompositionStableSlices } from "./useCompositionStableSlices";
export { useCompositionVolatileSlices } from "./useCompositionVolatileSlices";

export function useCompositionContextSlices<T extends BaseEntity>(
  params: CompositionContextValueParams<T>
): CompositionContextSlices<T> {
  const stable = useCompositionStableSlices<T>(params);
  const volatileSlices = useCompositionVolatileSlices<T>(params);

  // Not memoized on purpose: every FIELD is stable, and the only consumer
  // (useCompositionContextValue) destructures it immediately.
  return { ...stable, ...volatileSlices };
}
